/**
 * 提取自view-design的平滑滚动函数，用于滚动到页面的特定位置
 * @param {Object || window} el 要滚动的元素
 * @param {Number} from 滚动的起点
 * @param {Number} to 滚动的终点
 * @param {Number} duration 滚动动画持续时间，单位 毫秒
 * @param {Function} endCallback 滚动完成后的回调
 * */
import router from 'src/router';
import Vue from 'vue';
import { TypeItem } from 'src/types/views/common';

export function scrollTop(el: Element | Window, from = 0, to: number,
  duration = 500, endCallback?: () => void) {
  if (!window.requestAnimationFrame) {
    window.requestAnimationFrame = (
      window.webkitRequestAnimationFrame
      || (window as any).mozRequestAnimationFrame
      || (window as any).msRequestAnimationFrame
      || function (callback) {
        return window.setTimeout(callback, 1000 / 60);
      }
    );
  }
  const difference = Math.abs(from - to);
  const step = Math.ceil(difference / duration * 50);

  function scroll(start: number, end: number, distance: number) {
    if (start === end) {
      if (endCallback) {
        endCallback();
      }
      return;
    }

    let d = (start + distance > end) ? end : start + distance;
    if (start > end) {
      d = (start - distance < end) ? end : start - distance;
    }

    if (el instanceof Window) {
      window.scrollTo(d, d);
    } else {
      // eslint-disable-next-line no-param-reassign
      el.scrollTop = d;
    }
    window.requestAnimationFrame(() => scroll(d, end, distance));
  }

  scroll(from, to, step);
}

/**
 * 用来生成指定范围内的随机数
 * @param {Number} min 范围的下界
 * @param {Number} max 范围的上界
 */
export function random(min: number, max: number) {
  return Math.round((Math.random()) * (max - min)) + min;
}

type TypeList = { id: string; name: string; children: TypeItem[] }[];

type AreaList = { id: string; name: string; children: TypeList }[];

/**
 * 将二级数据转化为后台存储的字符串
 * ['1,11','2']
 * ['哈(哈1，哈2)','呵(呵1，呵2)']
 * */
export function computeTwoLevel(data: TypeList): string[] {
  const ids: string[] = [];
  const names: string[] = [];
  data.forEach((item) => {
    if (item.children && item.children.length === 0) {
      ids.push(item.id);
      names.push(item.name);
    } else {
      ids.push(`${item.id},${item.children.map(child => child.id).join(',')}`);
      names.push(`${item.name}(${item.children.map(child => child.name).join('，')})`);
    }
  });
  return [
    ids.length === 0 ? '' : `/${ids.join('/')}/`,
    names.length === 0 ? '' : names.join(','),
  ];
}

/**
 * 将三级数据转化为后台存储的字符串
 * ['1-11-111','1-11-112','2']
 * ['哈[哈1(哈11)，哈2]','呵(呵1，呵2)']
 * */
export function computeThreeLevel(data: AreaList): string[] {
  const ids: string[] = [];
  const names: string[] = [];
  data.forEach((item) => {
    if (item.children && item.children.length === 0) {
      ids.push(item.id);
      names.push(item.name);
      // 若第二层存在下级结点，则在第二层进行拆分
    } else if (item.children.some(child => child.children && child.children.length !== 0)) {
      item.children.forEach((child) => {
        if (child.children && child.children.length === 0) {
          ids.push(`${item.id}-${child.id}`);
          names.push(`${item.name}-${child.name}`);
        } else {
          ids.push(`${item.id}-${child.id}-${child.children.map(grand => grand.id).join(',')}`);
          names.push(`${item.name}-${child.name}-${child.children.map(grand => grand.name).join('，')}`);
        }
      });
      // 否则，直接拼接第二层
    } else {
      ids.push(`${item.id}-${item.children.map(child => child.id).join(',')}`);
      names.push(`${item.name}-${item.children.map(child => child.name).join('，')}`);
    }
  });
  return [
    ids.length === 0 ? '' : `/${ids.join('/')}/`,
    names.length === 0 ? '' : names.join('|'),
  ];
}

/**
 * 将后台返回的字符串转化为二级数据
 * */
export function computeTwoLevelList(idList: string[], nameList: string[]): TypeList {
  const list: TypeList = [];
  idList.forEach((idItem: string, index: number) => {
    const ids = idItem.split(/[,，]/);
    if (ids.length === 1) {
      list.push({
        id: idItem,
        name: nameList[index],
        children: [],
      });
    } else {
      const names = nameList[index].replace(')', '')
        .replace('(', '，').split('，');
      list.push({
        id: ids[0],
        name: names[0],
        children: ids.splice(1).map((childId: string, childIndex: number) => ({
          id: childId,
          name: names[childIndex + 1],
        })),
      });
    }
  });
  return list;
}

/**
 * 将后台返回的字符串转化为三级数据
 * */
export function computeThreeLevelList(idList: string[], nameList: string[]): AreaList {
  const list: AreaList = [];
  idList.forEach((id: string, index: number) => {
    const ids = id.split('-');
    const names = nameList[index].split('-');
    if (ids.length === 1) {
      list.push({
        id,
        name: nameList[index],
        children: [],
      });
    } else if (ids.length === 2) {
      // 是否已经在数组中
      const item = list.find(t => t.id === ids[0]);
      const childNameList = names[1].split('，');
      if (item) {
        item.children.push(...(ids[1].split(/[,，]/).map((childId: string, childIndex: number) => ({
          id: childId,
          name: childNameList[childIndex],
          children: [],
        }))));
      } else {
        list.push({
          id: ids[0],
          name: names[0],
          children: ids[1].split(/[,，]/).map((childId: string, childIndex: number) => ({
            id: childId,
            name: childNameList[childIndex],
            children: [],
          })),
        });
      }
    } else {
      // 是否已经在数组中
      const item = list.find(t => t.id === ids[0]);
      const childNameList = names[2].split('，');
      if (item) {
        const secondItem = item.children.find(t => t.id === ids[1]);
        if (secondItem) {
          secondItem.children.push(...(ids[2].split(/[,，]/).map((childId: string, childIndex: number) => ({
            id: childId,
            name: childNameList[childIndex],
          }))));
        } else {
          item.children.push({
            id: ids[1],
            name: names[1],
            children: ids[2].split(/[,，]/).map((childId: string, childIndex: number) => ({
              id: childId,
              name: childNameList[childIndex],
            })),
          });
        }
      } else {
        list.push({
          id: ids[0],
          name: names[0],
          children: [{
            id: ids[1],
            name: names[1],
            children: ids[2].split(/[,，]/).map((childId: string, childIndex: number) => ({
              id: childId,
              name: childNameList[childIndex],
            })),
          }],
        });
      }
    }
  });
  return list;
}

/**
 * 登录失效时弹出框提示及跳转
 * */
export function loginFail() {
  // 仅展示一个弹框
  if (document.getElementsByClassName('v-modal').length === 0) {
    Vue.prototype.$confirm('您的登录状态已失效，请重新登录', '登录失效', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning',
    }).then(() => {
      if (router.currentRoute.name !== 'loginByPassword') {
        router.replace({
          name: 'login',
          query: { redirect: router.currentRoute.fullPath },
        });
      }
    });
  }
}
